/**
 * The workbook render itself — sheet tabs, the grid, the cited cell
 * highlighted — with no opinion about the container it sits in.
 *
 * Split out of `SpreadsheetViewer`, which is the modal shell around it. Both the
 * chat's citations and the KPI grid's open through that shell, so there is one
 * renderer and one set of behaviours — a second grid implementation would be a
 * second set of bugs about frozen headers and highlight rects.
 *
 * Loading is delegated to the caller's `loadWorkbook`, so every open shares one
 * cache: a workbook already fetched costs nothing to show again.
 */

import { Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import { friendlyErrorMessage } from "../utils/apiErrorDetail";
import { ParsedDopplerUri } from "../utils/dopplerUri";
import type { LoadWorkbook } from "./SpreadsheetViewer";

// Bound the rendered grid so a pathological used-range can't lock the tab.
const MAX_ROWS = 600;
const MAX_COLS = 80;

export const colLabel = (idx: number): string => {
	let n = idx;
	let s = "";
	while (n >= 0) {
		s = String.fromCharCode(65 + (n % 26)) + s;
		n = Math.floor(n / 26) - 1;
	}
	return s;
};

export interface FocusRect {
	r0: number;
	c0: number;
	r1: number;
	c1: number;
}

/** Parse the ``cell``/``range`` from the URI into a 0-based inclusive rect. */
export const focusRectFor = (parsed: ParsedDopplerUri): FocusRect | null => {
	try {
		if (parsed.range) {
			const r = XLSX.utils.decode_range(parsed.range);
			return { r0: r.s.r, c0: r.s.c, r1: r.e.r, c1: r.e.c };
		}
		if (parsed.cell) {
			const c = XLSX.utils.decode_cell(parsed.cell);
			return { r0: c.r, c0: c.c, r1: c.r, c1: c.c };
		}
	} catch {
		return null;
	}
	return null;
};

export interface SheetGrid {
	rows: string[][];
	rowCount: number;
	colCount: number;
	truncated: boolean;
	/**
	 * Where the grid starts in A1 terms (0-based). A sheet whose first column
	 * or row is empty has a used range that starts past A1, and the grid is
	 * built from that range — so `rows[0][0]` may be B3, not A1. Every place
	 * that speaks A1 (the cited cell, the column letters, the row numbers)
	 * must add this back, or the highlight lands one column over (DOP-522).
	 */
	origin: { r: number; c: number };
}

export const buildGrid = (ws: XLSX.WorkSheet): SheetGrid => {
	const ref = ws["!ref"];
	if (!ref)
		return {
			rows: [],
			rowCount: 0,
			colCount: 0,
			truncated: false,
			origin: { r: 0, c: 0 },
		};
	const range = XLSX.utils.decode_range(ref);
	const rowCount = range.e.r - range.s.r + 1;
	const colCount = range.e.c - range.s.c + 1;
	const rows: string[][] = [];
	const rMax = Math.min(range.e.r, range.s.r + MAX_ROWS - 1);
	const cMax = Math.min(range.e.c, range.s.c + MAX_COLS - 1);
	for (let r = range.s.r; r <= rMax; r += 1) {
		const row: string[] = [];
		for (let c = range.s.c; c <= cMax; c += 1) {
			const cell = ws[XLSX.utils.encode_cell({ r, c })];
			row.push(cell ? String(cell.w ?? cell.v ?? "") : "");
		}
		rows.push(row);
	}
	return {
		rows,
		rowCount,
		colCount,
		truncated: rowCount > MAX_ROWS || colCount > MAX_COLS,
		origin: { r: range.s.r, c: range.s.c },
	};
};

export interface SpreadsheetViewProps {
	parsed: ParsedDopplerUri;
	chatId?: string | null;
	loadWorkbook: LoadWorkbook;
	/** Classes for the scrolling grid area. */
	className?: string;
	/** Server-resolved filename, reported up for the shell's header. */
	onResolvedFilename?: (filename: string | null) => void;
	/** Reported up so a shell can title itself with the active sheet. */
	onActiveSheet?: (sheet: string | null) => void;
}

const SpreadsheetView: React.FC<SpreadsheetViewProps> = ({
	parsed,
	chatId,
	loadWorkbook,
	className,
	onResolvedFilename,
	onActiveSheet,
}) => {
	const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
	const [activeSheet, setActiveSheet] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const focusCellRef = useRef<HTMLTableCellElement | null>(null);

	// Workbook bytes are keyed on file identity only — sheet/cell/range are
	// cheap view state and must NOT trigger a refetch.
	const cacheKey = `${parsed.kind}:${parsed.subkind}:${chatId ?? ""}:${
		parsed.target
	}`;

	useEffect(() => {
		const entry = loadWorkbook(parsed, chatId);
		// Synchronous cache hit: skip the loading state entirely.
		if (entry.value) {
			setWorkbook(entry.value);
			onResolvedFilename?.(entry.filename ?? null);
			setError(null);
			setLoading(false);
			return;
		}
		let cancelled = false;
		setLoading(true);
		setError(null);
		setWorkbook(null);
		entry.promise.then(
			(wb) => {
				if (cancelled) return;
				setWorkbook(wb);
				onResolvedFilename?.(entry.filename ?? null);
				setLoading(false);
			},
			(err) => {
				if (cancelled) return;
				setError(friendlyErrorMessage(err, "Failed to load the workbook."));
				setLoading(false);
			},
		);
		// Guard setState only; the fetch must complete and populate the cache even
		// if this view unmounts mid-load (a later open will reuse it).
		return () => {
			cancelled = true;
		};
		// loadWorkbook ignores sheet/cell/range, so a stale `parsed` is safe;
		// cacheKey captures every input that affects the loaded bytes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cacheKey, loadWorkbook]);

	// Cheap projection: pick the active sheet when the workbook arrives or a new
	// citation opens. Keyed on `parsed` (a fresh object per open) so re-opening
	// the citation re-asserts its sheet even after a manual tab switch.
	useEffect(() => {
		if (!workbook) return;
		const initial =
			parsed.sheet && workbook.SheetNames.includes(parsed.sheet)
				? parsed.sheet
				: workbook.SheetNames[0] ?? null;
		setActiveSheet(initial);
		onActiveSheet?.(initial);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workbook, parsed]);

	// Only highlight when viewing the sheet the citation pointed at.
	const focus = useMemo<FocusRect | null>(
		() => (activeSheet === parsed.sheet ? focusRectFor(parsed) : null),
		[activeSheet, parsed],
	);

	const grid = useMemo<SheetGrid | null>(() => {
		if (!workbook || !activeSheet) return null;
		const ws = workbook.Sheets[activeSheet];
		return ws ? buildGrid(ws) : null;
	}, [workbook, activeSheet]);

	// Centre the highlighted cell once the grid renders — both axes.
	//
	// A citation points at one cell in a sheet that can be hundreds of rows and
	// dozens of columns wide, and the viewer's job is to put THAT cell in front
	// of the reader with its neighbours around it: the row label to its left and
	// the period heading above it are most of what makes a number checkable.
	// Top-left anchoring leaves the cited cell against an edge, or just off one.
	//
	// This is the modal's own scroll container, not the page's — the dialog is
	// `position: fixed`, so nothing here moves the document behind it.
	useEffect(() => {
		if (focus && focusCellRef.current) {
			focusCellRef.current.scrollIntoView({
				block: "center",
				inline: "center",
			});
		}
	}, [grid, focus]);

	// Grid coordinates → A1 coordinates: the grid starts at the used range,
	// the citation speaks A1.
	const originR = grid?.origin.r ?? 0;
	const originC = grid?.origin.c ?? 0;
	const colFocused = (c: number): boolean =>
		!!focus && c + originC >= focus.c0 && c + originC <= focus.c1;
	const rowFocused = (r: number): boolean =>
		!!focus && r + originR >= focus.r0 && r + originR <= focus.r1;
	const isFocused = (r: number, c: number): boolean =>
		rowFocused(r) && colFocused(c);

	return (
		<>
			{workbook && workbook.SheetNames.length > 1 && (
				<div className="flex shrink-0 gap-1 overflow-x-auto border-b border-gray-200 bg-gray-50 px-3 py-2">
					{workbook.SheetNames.map((name) => (
						<button
							key={name}
							type="button"
							onClick={() => {
								setActiveSheet(name);
								onActiveSheet?.(name);
							}}
							className={`whitespace-nowrap rounded-md px-3 py-1 text-xs ${
								name === activeSheet
									? "bg-teal-600 text-white"
									: "text-gray-600 hover:bg-gray-200"
							}`}
						>
							{name}
						</button>
					))}
				</div>
			)}

			<div className={className ?? "min-h-0 flex-1 overflow-auto"}>
				{loading && (
					<div className="flex h-32 items-center justify-center gap-2 text-sm text-gray-500">
						<Loader2 className="h-4 w-4 animate-spin" /> Loading workbook…
					</div>
				)}
				{error && (
					<div
						className="flex h-32 items-center justify-center px-6 text-center text-sm text-red-600"
						role="alert"
					>
						{error}
					</div>
				)}
				{grid && !loading && !error && (
					<table className="border-collapse text-xs">
						<thead className="sticky top-0 z-10 bg-gray-50">
							<tr>
								<th className="sticky left-0 z-20 w-10 border border-gray-200 bg-gray-50" />
								{grid.rows[0]?.map((_, c) => (
									<th
										key={c}
										className={`border border-gray-200 px-2 py-1 font-normal text-gray-500 ${
											colFocused(c) ? "bg-teal-50 text-teal-700" : ""
										}`}
									>
										{colLabel(c + originC)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{grid.rows.map((row, r) => (
								<tr key={r}>
									<td
										className={`sticky left-0 z-10 border border-gray-200 bg-gray-50 px-2 py-1 text-right text-gray-400 ${
											rowFocused(r) ? "bg-teal-50 text-teal-700" : ""
										}`}
									>
										{r + originR + 1}
									</td>
									{row.map((cell, c) => {
										const focused = isFocused(r, c);
										const isAnchor =
											focused &&
											r + originR === focus?.r0 &&
											c + originC === focus?.c0;
										return (
											<td
												key={c}
												ref={isAnchor ? focusCellRef : undefined}
												data-focused={focused ? "true" : undefined}
												className={`whitespace-nowrap border px-2 py-1 ${
													focused
														? "border-teal-400 bg-teal-100 font-medium text-teal-900"
														: "border-gray-200 text-gray-800"
												}`}
											>
												{cell}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{grid && (grid.truncated || grid.rowCount > 0) && (
				<div className="shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500">
					{activeSheet} • {grid.rowCount} rows × {grid.colCount} cols
					{grid.truncated
						? " (view truncated — download for the full sheet)"
						: ""}
				</div>
			)}
		</>
	);
};

export default SpreadsheetView;
